import { EmailData, EmailAddress, Attachment } from '../types/email.types';

export class OfficeEmailExtractor {
  private item: Office.MessageRead;
  
  constructor() {
    this.item = Office.context.mailbox.item as Office.MessageRead;
  }

  async extractEmailData(): Promise<EmailData> {
    if (!this.item) {
      throw new Error('No email item available');
    }

    const [headers, body] = await Promise.all([
      this.extractHeaders(),
      this.extractBody()
    ]);

    return {
      headers,
      body,
      attachments: await this.extractAttachments(),
      metadata: this.extractMetadata() || {}  // Ensure always an object
    };
  }

  private async extractHeaders() {
    const from = this.convertToEmailAddress(this.item.from);
    const to = (this.item.to || []).map(addr => this.convertToEmailAddress(addr));
    const cc = (this.item.cc || []).map(addr => this.convertToEmailAddress(addr));

    return {
      from,
      to,
      cc: cc, // Always send array, even if empty
      subject: this.item.subject || 'No Subject',
      date: this.item.dateTimeCreated?.toISOString() || new Date().toISOString(),
      messageId: this.item.internetMessageId || `<${Date.now()}@client>`
    };
  }

  private convertToEmailAddress(addr: Office.EmailAddressDetails): EmailAddress {
    return {
      emailAddress: addr.emailAddress,
      displayName: addr.displayName || addr.emailAddress
    };
  }

  private async extractBody(): Promise<{ html: string; text: string }> {
    return new Promise((resolve, reject) => {
      this.item?.body.getAsync(
        Office.CoercionType.Html,
        (htmlResult) => {
          if (htmlResult.status === Office.AsyncResultStatus.Failed) {
            reject(new Error('Failed to get email body'));
            return;
          }

          this.item?.body.getAsync(
            Office.CoercionType.Text,
            (textResult) => {
              resolve({
                html: htmlResult.value || '',
                text: textResult.value || ''
              });
            }
          );
        }
      );
    });
  }

  private async extractAttachments(): Promise<Attachment[]> {
    const attachments = this.item?.attachments || [];
    
    // For MVP, we just collect attachment metadata
    // Content could be fetched using getAttachmentContentAsync if needed
    return attachments.map(att => ({
      id: att.id,
      name: att.name,
      size: att.size || 0,
      contentType: att.contentType || 'application/octet-stream',
      isInline: att.isInline || false
    }));
  }

  private extractMetadata(): Record<string, string> {
    const metadata: Record<string, string> = {};
    
    // For MessageRead, importance is available
    const itemWithImportance = this.item as any;
    if (itemWithImportance.importance) {
      metadata.importance = itemWithImportance.importance;
    }
    
    if (this.item?.conversationId) {
      metadata.conversationId = this.item.conversationId;
    }

    // Categories handling - categories is an object with methods in Office.js
    // For MVP, we'll skip categories as they require async calls
    
    return metadata;
  }
}
